import './pdfjsPolyfills'
import { GlobalWorkerOptions } from 'vue-pdf-embed/dist/index.essential.mjs'
import { version as pdfjsVersion } from 'pdfjs-dist/legacy/build/pdf.mjs'
// `?worker&url`, not `?url`: the worker goes through the build like the rest
// of the bundle (syntax lowered to `build.target`, polyfills for the Chrome 92
// screens). A plain `?url` asset would be copied untouched.
import pdfWorkerUrl from './pdfjs.worker?worker&url'

/**
 * Single entry point for pdf.js in the app: the viewers import
 * `vue-pdf-embed/dist/index.essential.mjs`, which runs the `pdfjs-dist`
 * version declared in package.json, and this module gives it the worker and
 * runtime assets of that very same version.
 *
 * The default `vue-pdf-embed` entry bundles its own older pdf.js and its own
 * worker: the decoders served under /pdfjs/ (vite.config.ts) would not match.
 */
GlobalWorkerOptions.workerSrc = pdfWorkerUrl

export { default as VuePdfEmbed } from 'vue-pdf-embed/dist/index.essential.mjs'

type PdfData = Uint8Array | ArrayBuffer

export interface PdfDocumentSource {
  data?: PdfData
  url?: string
  wasmUrl: string
  standardFontDataUrl: string
  cMapUrl: string
  cMapPacked: true
}

/**
 * Absolute URL of a pdf.js asset directory. pdf.js only lets its worker fetch
 * the assets itself when every asset URL is an absolute http(s) URL; the main
 * thread then makes no request, so the Faro XHR monitor never sees one.
 *
 * The path carries the version of the running pdf.js, the same one the Vite
 * plugin serves and builds (vite.config.ts): a cached decoder from a previous
 * release can never be handed to a newer worker.
 */
function assetDirectoryUrl(directory: string, origin: string): string {
  return new URL(`/pdfjs/${pdfjsVersion}/${directory}/`, origin).href
}

/**
 * Wrap PDF bytes or a URL into the document parameters every viewer must use.
 * Without `wasmUrl`, pdf.js drops JBIG2 and JPEG 2000 images: scanned
 * letterheads and document backgrounds render blank.
 */
export function pdfDocumentSource(
  source: PdfData | string,
  origin: string = window.location.origin
): PdfDocumentSource {
  return {
    ...(typeof source === 'string' ? { url: source } : { data: source }),
    wasmUrl: assetDirectoryUrl('wasm', origin),
    standardFontDataUrl: assetDirectoryUrl('standard_fonts', origin),
    cMapUrl: assetDirectoryUrl('cmaps', origin),
    cMapPacked: true
  }
}
